<template>
  <main class="login-page">
    <div class="login-shell">
      <section class="login-form-panel">
        <div class="login-card">
          <header class="login-header text-center">
            <img src="/assets/Logo.png" alt="Mi Stock" class="login-logo" />
            <h1 class="login-title">Bienvenido</h1>
            <p class="login-subtitle">Inicia sesión para gestionar tu inventario</p>
          </header>

          <form class="login-form" role="form" @submit.prevent="handleLogin()">
            <div class="login-field">
              <label for="login-email" class="login-label">Correo electrónico</label>
              <argon-input
                id="login-email"
                v-model="user.email"
                type="email"
                class="mb-0"
                placeholder="nombre@empresa.com"
                name="email"
                size="lg"
              />
              <validation-error :errors="apiValidationErrors.email" />
            </div>

            <div class="login-field">
              <label for="login-password" class="login-label">Contraseña</label>
              <argon-input
                id="login-password"
                v-model="user.password"
                type="password"
                placeholder="••••••••"
                name="password"
                size="lg"
              />
            </div>

            <div class="login-options">
              <argon-switch id="rememberMe" class="login-remember">Recuérdame</argon-switch>
            </div>

            <argon-button
              type="submit"
              class="login-submit btn-fashion"
              color="primary"
              fullWidth
              size="lg"
            >
              Iniciar sesión
            </argon-button>

            <p class="login-footer text-center">
              <router-link :to="{ name: 'Forgot Password' }" class="login-link">
                ¿Olvidaste tu contraseña?
              </router-link>
            </p>
          </form>

          <button v-if="showInstallButton" type="button" class="install-btn" @click="installApp">
            <i class="fas fa-mobile-alt"></i>
            Instalar app
          </button>
        </div>
      </section>

      <aside class="login-visual-panel d-none d-lg-flex" aria-hidden="true">
        <div class="login-visual-inner">
          <transition-group name="fade" tag="div" class="login-carousel">
            <div
              v-for="(image, index) in carouselImages"
              v-show="currentImage === index"
              :key="image.src"
              class="login-carousel-slide"
            >
              <img :src="image.src" :alt="image.alt" class="login-carousel-img" />
            </div>
          </transition-group>
          <div class="login-visual-caption">
            <p>Controla, gestiona y crece con Mi Stock</p>
          </div>
        </div>
      </aside>
    </div>
  </main>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import formMixin from "../../mixins/form-mixin.js";
import ValidationError from "../../components/ValidationError.vue";
import showSwal from "../../mixins/showSwal.js";
import Swal from "sweetalert2";

const body = document.getElementsByTagName("body")[0];

export default {
  name: "Login",
  components: {
    ArgonInput,
    ArgonSwitch,
    ArgonButton,
    ValidationError,
  },
  mixins: [formMixin, showSwal],
  data() {
    return {
      user: {
        email: "",
        password: "",
      },
      currentImage: 0,
      carouselTimer: null,
      carouselImages: [
        { src: "/assets/fashion-image-1.png", alt: "Gestión de inventario" },
        { src: "/assets/fashion-image-2.png", alt: "Control de stock" },
        { src: "/assets/fashion-image-3.png", alt: "Ventas y operaciones" },
      ],
      installPromptEvent: null,
      isStandalone: false,
      isIos: false,
    };
  },
  computed: {
    showInstallButton() {
      if (this.isStandalone) return false;
      return Boolean(this.installPromptEvent) || this.isIos;
    },
  },
  created() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = false;
    this.$store.state.showFooter = false;
    body.classList.remove("bg-gray-100");
  },
  beforeUnmount() {
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
    }
    window.removeEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
    window.removeEventListener("appinstalled", this.onAppInstalled);
    this.$store.state.hideConfigButton = false;
    this.$store.state.showNavbar = true;
    this.$store.state.showSidenav = true;
    this.$store.state.showFooter = true;
    body.classList.add("bg-gray-100");
  },
  mounted() {
    this.carouselTimer = setInterval(() => {
      this.currentImage = (this.currentImage + 1) % this.carouselImages.length;
    }, 6000);

    this.isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    this.isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    window.addEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
    window.addEventListener("appinstalled", this.onAppInstalled);
  },
  methods: {
    onBeforeInstallPrompt(event) {
      event.preventDefault();
      this.installPromptEvent = event;
    },
    onAppInstalled() {
      this.installPromptEvent = null;
    },
    async installApp() {
      if (this.installPromptEvent) {
        this.installPromptEvent.prompt();
        const { outcome } = await this.installPromptEvent.userChoice;
        if (outcome === "accepted") {
          this.installPromptEvent = null;
        }
        return;
      }

      if (this.isIos) {
        await Swal.fire({
          icon: "info",
          title: "Instalar MiStock",
          html:
            'En iPhone/iPad: toca el ícono de <b>Compartir</b> ' +
            'y luego <b>"Agregar a pantalla de inicio"</b>.',
          confirmButtonColor: "#cc9900",
        });
      }
    },
    async handleLogin() {
      try {
        this.resetApiValidation();
        await this.$store.dispatch("auth/login", this.user);

        const userStr = localStorage.getItem("user");
        if (!userStr) {
          throw new Error("Usuario no encontrado después del login");
        }

        const user = JSON.parse(userStr);

        if (user && user.role === "3") {
          this.$router.push({ name: "Operador Dashboard" });
        } else {
          this.$router.push({ name: "Dashboard" });
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: "Error de credenciales",
          text:
            error.response?.data?.message ||
            "Credenciales no combinan. Intenta de nuevo.",
        });
        if (error.response?.data?.errors) {
          this.setApiValidation(error.response.data.errors);
        }
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background:
    radial-gradient(circle at 20% 20%, rgba(204, 153, 0, 0.08), transparent 40%),
    radial-gradient(circle at 80% 80%, rgba(130, 214, 22, 0.06), transparent 35%),
    linear-gradient(145deg, #1a2744 0%, #121c33 45%, #0d1524 100%);
}

.login-shell {
  display: grid;
  grid-template-columns: minmax(320px, 440px) 1fr;
  width: 100%;
  max-width: 1080px;
  min-height: min(640px, calc(100vh - 2.5rem));
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 32px 64px rgba(0, 0, 0, 0.28),
    0 0 0 1px rgba(255, 255, 255, 0.06);
}

.login-form-panel {
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1.75rem, 4vw, 2.75rem) clamp(1.5rem, 4vw, 2.5rem);
}

.login-card {
  width: 100%;
  max-width: 360px;
}

.login-header {
  margin-bottom: 1.75rem;
}

.login-logo {
  max-height: 64px;
  width: auto;
  margin-bottom: 1.25rem;
}

.login-title {
  margin: 0 0 0.35rem;
  font-size: 1.625rem;
  font-weight: 700;
  color: #344767;
  letter-spacing: -0.02em;
}

.login-subtitle {
  margin: 0;
  font-size: 0.925rem;
  color: #8392ab;
  line-height: 1.45;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.login-field {
  margin-bottom: 1.15rem;
}

.login-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #344767;
}

.login-options {
  display: flex;
  align-items: center;
  margin-bottom: 1.35rem;
}

.login-remember :deep(.form-check-label) {
  font-size: 0.875rem;
  color: #67748e;
  user-select: none;
}

.login-submit {
  margin-top: 0.15rem;
}

.login-footer {
  margin: 1.25rem 0 0;
}

.login-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3a59;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-link:hover {
  color: #cc9900;
}

.install-btn {
  margin-top: 1.25rem;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease;
}

.install-btn:hover {
  border-color: #cc9900;
  color: #cc9900;
  background-color: #fffdf5;
}

.btn-fashion {
  background: linear-gradient(135deg, #b8860b 0%, #d4a017 50%, #e6b422 100%) !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
  border-radius: 10px !important;
  padding-top: 0.85rem !important;
  padding-bottom: 0.85rem !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 8px 20px rgba(204, 153, 0, 0.28);
}

.btn-fashion:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(204, 153, 0, 0.35);
}

.login-form :deep(.form-control) {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.72rem 0.95rem;
  font-size: 0.9375rem;
  background-color: #f8fafc;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.login-form :deep(.form-control:focus) {
  border-color: #cc9900;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(204, 153, 0, 0.15);
}

.login-form :deep(.form-control-lg) {
  min-height: 48px;
}

.login-visual-panel {
  position: relative;
  align-items: center;
  justify-content: center;
  padding: 2rem 2.5rem;
  background:
    linear-gradient(160deg, rgba(255, 255, 255, 0.04) 0%, transparent 60%),
    linear-gradient(145deg, #1e2d4d 0%, #16243f 100%);
}

.login-visual-inner {
  position: relative;
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.login-carousel {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  max-height: 380px;
}

.login-carousel-slide {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-carousel-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  filter: drop-shadow(0 16px 32px rgba(0, 0, 0, 0.2));
}

.login-visual-caption {
  text-align: center;
}

.login-visual-caption p {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.72);
  letter-spacing: 0.02em;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 991.98px) {
  .login-shell {
    grid-template-columns: 1fr;
    max-width: 440px;
    min-height: auto;
  }

  .login-form-panel {
    padding: 2rem 1.75rem 2.25rem;
  }

  .login-card {
    max-width: none;
  }
}

@media (max-width: 575.98px) {
  .login-page {
    padding: 0.75rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .login-shell {
    border-radius: 16px;
  }

  .login-form-panel {
    padding: 1.75rem 1.25rem 2rem;
  }

  .login-title {
    font-size: 1.45rem;
  }
}
</style>
